import { Skeleton } from 'antd'
import React, { useState } from 'react'
import type { DropdownMenuItem } from 'sutra/components'
import { DropdownMenu } from 'sutra/components'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Select } from 'sutra/components/Select/Select'
import { UsernameInitials } from 'sutra/components/UsernameInitials/UsernameInitials'

import { formatPeopleCount } from 'components/share-resource/format-people-count'
import {
  AddButton,
  Caption,
  Controls,
  CountDivider,
  MenuTrigger,
  PersonEmail,
  PersonInfo,
  PersonName,
  PickerField,
  PickerRow,
  RemoveButton,
  RoleText,
  ShareRow,
  SharesList,
  SkeletonRow,
} from 'components/share-resource/ShareResource/styles'
import type {
  ShareResourceMember,
  ShareResourceProps,
} from 'components/share-resource/types'

// Sentinel for the "Remove" item mixed into a row's role dropdown — internal
// to this component, distinct from any real role's `value`.
const REMOVE_OPTION = '__share-resource-remove__'

const SKELETON_ROW_COUNT = 3

function MemberRow({
  member,
  roles,
  onRoleChange,
  onRemove,
}: {
  member: ShareResourceMember
  roles?: ShareResourceProps['roles']
  onRoleChange?: (memberId: string, role: string) => void
  onRemove?: (memberId: string) => void
}): React.ReactElement {
  const roleLabel = (value: string): string =>
    roles?.find((role) => role.value === value)?.label ?? value

  let trailing: React.ReactNode = null
  if (member.staticRole) {
    trailing = <RoleText>{member.staticRole}</RoleText>
  } else if (roles && onRoleChange) {
    const items: DropdownMenuItem[] = [
      ...roles.map((role) => ({ key: role.value, label: role.label })),
      ...(onRemove
        ? [
            { type: 'divider' as const },
            {
              key: REMOVE_OPTION,
              label: 'Remove',
              danger: true,
              // An action, not a role to hold: keep it out of the selection.
              selectable: false,
            },
          ]
        : []),
    ]
    trailing = (
      <DropdownMenu
        items={items}
        selectedKey={member.roleValue || null}
        onAction={(key) => {
          if (key === REMOVE_OPTION) {
            onRemove?.(member.id)
            return
          }
          if (key !== member.roleValue) onRoleChange(member.id, key)
        }}
      >
        <MenuTrigger type="button">
          {member.roleValue ? roleLabel(member.roleValue) : ''}
        </MenuTrigger>
      </DropdownMenu>
    )
  } else if (roles && member.roleValue) {
    trailing = (
      <Chiclet $tone="gray" $size="lg">
        {roleLabel(member.roleValue)}
      </Chiclet>
    )
  } else if (onRemove) {
    trailing = (
      <RemoveButton type="button" onClick={() => onRemove(member.id)}>
        Remove
      </RemoveButton>
    )
  }

  return (
    <ShareRow>
      <UsernameInitials initials={member.initials} size="lg" />
      <PersonInfo>
        <PersonName>{member.name}</PersonName>
        {member.secondaryLabel && (
          <PersonEmail>{member.secondaryLabel}</PersonEmail>
        )}
      </PersonInfo>
      {trailing}
    </ShareRow>
  )
}

/**
 * Presentational "invite teammates + who has access" UI. Works inline (the
 * agent create/edit form's Sharing section) or wrapped by
 * `ShareResourceModal` — no data fetching, no service hooks; every id is a
 * caller-owned string (email or `String(userId)`).
 */
export function ShareResource({
  members,
  roles,
  invite,
  manage,
  isLoading = false,
  zIndex,
}: ShareResourceProps): React.ReactElement {
  const [selectedId, setSelectedId] = useState('')
  const [pendingRole, setPendingRole] = useState(
    invite?.defaultRole ?? roles?.[0]?.value ?? '',
  )

  const handleAdd = (): void => {
    if (!selectedId || !invite) return
    invite.onAdd(selectedId, roles ? pendingRole : undefined)
    setSelectedId('')
    setPendingRole(invite.defaultRole ?? roles?.[0]?.value ?? '')
  }

  const roleAddItems: DropdownMenuItem[] = (roles ?? []).map((role) => ({
    key: role.value,
    label: role.label,
  }))

  return (
    <>
      {invite && (
        <>
          <Caption>{invite.caption}</Caption>
          <PickerRow>
            <PickerField>
              <Select
                value={selectedId}
                onChange={(picked) => setSelectedId(picked)}
                options={invite.candidates.map((candidate) => ({
                  value: candidate.id,
                  label: candidate.label,
                }))}
                triggerLabel={
                  selectedId === ''
                    ? (invite.placeholder ?? 'Add a teammate')
                    : undefined
                }
                searchable
                zIndex={zIndex}
              />
            </PickerField>
            <Controls>
              {roles && (
                <DropdownMenu
                  items={roleAddItems}
                  selectedKey={pendingRole || null}
                  onAction={setPendingRole}
                >
                  <MenuTrigger type="button">
                    {roles.find((role) => role.value === pendingRole)?.label ??
                      ''}
                  </MenuTrigger>
                </DropdownMenu>
              )}
              <AddButton
                variant="primary"
                size="sm"
                height="32px"
                onClick={handleAdd}
                disabled={!selectedId || invite.isSubmitting}
              >
                Add
              </AddButton>
            </Controls>
          </PickerRow>
          {invite.extra}
        </>
      )}

      {!isLoading && (
        <CountDivider>{formatPeopleCount(members.length)}</CountDivider>
      )}

      <SharesList>
        {isLoading &&
          Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <SkeletonRow key={index}>
              <Skeleton.Avatar active size={30} shape="square" />
              <PersonInfo>
                <Skeleton.Input active size="small" />
              </PersonInfo>
            </SkeletonRow>
          ))}
        {!isLoading &&
          members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              roles={roles}
              onRoleChange={manage?.onRoleChange}
              onRemove={manage?.onRemove}
            />
          ))}
      </SharesList>
    </>
  )
}

ShareResource.displayName = 'ShareResource'
